"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { Mail } from "lucide-react";
import { Sheet } from "@/components/ui/Sheet";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { ACCOUNT_PATHS, REGISTRATION_PARAM, WAITING_LIST_PARAM } from "@/lib/address";
import { isPreview } from "@/lib/preview";
import { promptedHere, usePush, worthOffering } from "@/lib/push";
import { useSession } from "@/lib/session";
import { useLookedAround } from "@/lib/lookedAround";

/*
 * The question about our news by email (admin-api#51), a sheet that opens by itself, as
 * the notifications ask does (NotificationsAutoAsk), for an account holder who never
 * answered on record: everyone had a yes by default before 20 Sept 2026, which is no
 * answer at all. Yes and no are both recorded (source PROMPT). Agora não, or closing the
 * sheet, puts it aside on this device for 60 days.
 *
 * **One question a visit**: it waits while the notifications ask has still to open by
 * itself on this device, and never opens in the visit that ask opened in, so the two never
 * come one after the other. Never in a preview from the back-office (no `news_answered`),
 * on the settings page, or over another sheet; and like that ask, only once the client has
 * looked around on the visit (a second page, or an event: lib/lookedAround.ts).
 */

const ASIDE_KEY = "md:news-asked";
const ASIDE_DAYS = 60;
// after the page has settled, as the notifications ask waits
const DELAY_MS = 2500;

const putAside = () => {
  try {
    localStorage.setItem(ASIDE_KEY, String(Date.now()));
  } catch {
    // a private window: it is asked again next time, which is harmless
  }
};
const asideLately = () => {
  try {
    const at = Number(localStorage.getItem(ASIDE_KEY)) || 0;
    return Date.now() - at < ASIDE_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return true;
  }
};

export function NewsAsk() {
  const { client, reload } = useSession();
  const push = usePush();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  // read once, when the app opens: whether the notifications ask had already had its turn
  // on this device before this visit
  const [pushAskedBefore] = useState(promptedHere);
  const asked = useRef(false);
  const lookedAround = useLookedAround();

  const due =
    !!client &&
    lookedAround &&
    client.news_answered === false &&
    !isPreview() &&
    pathname !== ACCOUNT_PATHS.privacy &&
    push.ready &&
    (pushAskedBefore || !worthOffering(push));

  useEffect(() => {
    if (!due || asked.current || asideLately()) return;
    const timer = window.setTimeout(() => {
      const query = new URLSearchParams(window.location.search);
      // a sheet is open (the page behind does not scroll), or about to be: try on the next page
      if (document.body.style.overflow === "hidden" || query.has(REGISTRATION_PARAM) || query.has(WAITING_LIST_PARAM)) return;
      asked.current = true;
      setOpen(true);
      track("news_prompt", { source: "auto" });
    }, DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [due, pathname]);

  const close = () => {
    if (!done) putAside();
    setOpen(false);
  };
  const answer = async (yes: boolean) => {
    setBusy(true);
    try {
      await api.put("/public-api/client/profile/news", { yes });
      track(yes ? "news_yes" : "news_no", { source: "auto" });
      setDone(true);
      void reload();
      if (yes) {
        // a moment to read that it is done
        window.setTimeout(() => setOpen(false), 1600);
      } else {
        setOpen(false);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={close} title="Novidades por email" width={440}>
      {open && <NewsQuestion done={done} busy={busy} onAnswer={(yes) => void answer(yes)} onLater={close} />}
    </Sheet>
  );
}

/** What the sheet says: the question and its three answers, or that it is done. */
export function NewsQuestion({ done, busy, onAnswer, onLater }: { done: boolean; busy: boolean; onAnswer: (yes: boolean) => void; onLater: () => void }) {
  if (done) {
    return (
      <div className="flex items-center gap-2 px-5 pb-6 pt-2 text-[14.5px] font-medium text-brand-dark">
        <Mail size={20} /> Combinado: vais receber as nossas novidades por email.
      </div>
    );
  }
  return (
    <div className="px-5 pb-6 pt-2">
      <p className="text-[14.5px] leading-relaxed text-ink-soft">
        Queres receber as nossas novidades por email? Eventos novos e últimas vagas, uma ou duas vezes por mês. Podes deixar de
        receber quando quiseres.
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button onClick={() => onAnswer(true)} loading={busy} icon={<Mail size={17} />}>
          Sim, quero
        </Button>
        <Button variant="outline" disabled={busy} onClick={() => onAnswer(false)}>
          Não, obrigado
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onLater}>
          Agora não
        </Button>
      </div>
    </div>
  );
}
